<template>
  <div>
    <Notification
      id="review-link-toast"
      class="mt-16"
      :message="toastMessage"
      :triggerNotification="showToast"
      @toggle-visibility="showToast = false"
    />
    <Popup
      :modelValue="modelValue"
      popupHeader="How do you want to request a review?"
      popupSize="lg"
      headerClass="text-h2"
      @close="$emit('close')"
    >
      <p class="text-body text-grey-body mb-16">
        Select a method and Sittercity will help you send a one-time review link to anyone.
      </p>
      <Button value="Share review link" variant="outlined" class="w-full mb-16" size="medium" @click="getOneTimeUseLink"
        ><template #left-icon><share-icon class="mr-8" /></template
      ></Button>
      <Button value="Request on sittercity" variant="outlined" class="w-full" size="medium" :to="{ name: 'RequestTabs' }" />
    </Popup>
  </div>
</template>

<script lang="ts">
import Notification from '@/components/notification/Notification.vue';
import Popup from '@/components/popup/Popup.vue';
import Button from '@/components/button/Button.vue';
import ShareIcon from '@/graphics/icons/ShareIcon.vue';
import { CREATE_EXTERNAL_REVIEW_REQUEST_MUTATION } from '@/graphql/ratings/mutations';
import { sendSegmentEvent } from '@/lib/segment';

export default {
  name: 'RequestReviewPopup',
  components: {
    Notification,
    Popup,
    Button,
    ShareIcon,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    sitterId: {
      type: String,
      required: true,
    },
    sitterName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      toastMessage: '',
      showToast: false,
    };
  },
  methods: {
    async copyLinkToClipboard(oneTimeUseLink) {
      try {
        await navigator.clipboard.writeText(oneTimeUseLink);
        this.toastMessage = `Review request link copied to clipboard. Paste to share, links are good for one use.`;
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
      this.showToast = true;
      this.$emit('close');
      setTimeout(() => {
        const toastElement = document.getElementById('review-link-toast');
        if (toastElement) {
          toastElement.focus();
        }
        document.body.scrollTop = document.documentElement.scrollTop = 0;
      }, 1);
    },
    async shareOneTimeLink(oneTimeToken) {
      const oneTimeUseLink = `${window.location.origin}/ratings/sitters/${this.sitterId}/new_external/${oneTimeToken}`;
      const copyFallback = `Hi!
I'm really grateful for the work experience I gained with your family. Can you take a moment to leave me a review on my Sittercity profile?

Your input will help me connect with other great families for future work opportunities. 

${oneTimeUseLink}

Thank you!
`;
      const data = {
        title: `Review Request for ${this.sitterName}`,
        text: `Hi!
I'm really grateful for the work experience I gained with your family. Can you take a moment to leave me a review on my Sittercity profile?

Your input will help me connect with other great families for future work opportunities. 

Thank you!
`,
        url: oneTimeUseLink,
      };

      try {
        if (navigator.canShare(data)) {
          // if webshareapi enabled - use that to share URL with text message
          await navigator.share(data);
        } else {
          // if webshareapi not enabled - copy link to clipboard and open toast notification
          this.copyLinkToClipboard(copyFallback);
        }
      } catch (error: any) {
        if (error.message !== 'Abort due to cancellation of share.') {
          this.copyLinkToClipboard(copyFallback);
        }
      }
    },
    async getOneTimeUseLink() {
      // get link from API
      this.$apollo
        .mutate({
          mutation: CREATE_EXTERNAL_REVIEW_REQUEST_MUTATION,
          variables: {},
        })
        .then((payload) => {
          const oneTimeToken = payload.data.externalProviderReviewRequests.create.reviewToken;
          this.shareOneTimeLink(oneTimeToken);
          sendSegmentEvent('Sitter External Review Request Started', {
            sitter_uuid: this.sitterId,
            review_id: oneTimeToken,
          });
        })
        .catch((error) => {
          console.error('Error retrieving external reviews one time use token', error);
          this.toastMessage = 'Error retrieving one-time use link.';
          this.showToast = true;
        });
    },
  },
};
</script>
